import React, { useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { useMidiLyrics } from '../../../hooks/useMidiLyrics';
import { useMidiPlayer } from '../../../hooks/useMidiPlayer';

/**
 * Karaoke Modal Component
 * Displays animated, synchronized lyrics with MIDI playback
 */
export default function KaraokeModal({ isOpen, onClose, midiUrl, songTitle }) {
  const lyricsPlayer = useMidiLyrics(midiUrl);
  const audioPlayer = useMidiPlayer(midiUrl);

  // Sync karaoke player with audio player
  useEffect(() => {
    if (lyricsPlayer?.isPlaying && !audioPlayer?.isPlaying) {
      audioPlayer?.play();
    } else if (!lyricsPlayer?.isPlaying && audioPlayer?.isPlaying) {
      audioPlayer?.pause();
    }
  }, [lyricsPlayer?.isPlaying]);

  // Close modal with escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e?.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document?.addEventListener('keydown', handleEscape);
    return () => document?.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleClose = () => {
    lyricsPlayer?.stop();
    audioPlayer?.stop();
    onClose?.();
  };

  const handlePlayPause = () => {
    if (lyricsPlayer?.isPlaying) {
      lyricsPlayer?.pause();
      audioPlayer?.pause();
    } else {
      lyricsPlayer?.play();
      audioPlayer?.play();
    }
  };

  const handleStop = () => {
    lyricsPlayer?.stop();
    audioPlayer?.stop();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 backdrop-blur-sm">
      <div className="relative w-full max-w-6xl h-[90vh] bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 rounded-xl shadow-2xl overflow-hidden">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 p-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full transition-all duration-200"
          aria-label="Close karaoke"
        >
          <Icon name="X" size={24} className="text-white" />
        </button>

        {/* Title Section */}
        <div className="absolute top-4 left-4 right-16 z-10">
          <h2 className="text-2xl font-bold text-white drop-shadow-lg flex items-center">
            <Icon name="Music" size={28} className="mr-3 text-yellow-300" />
            {songTitle || 'Karaoke Mode'}
          </h2>
          <p className="text-sm text-blue-200 mt-1 ml-11">
            Synchronized Lyrics Karaoke
          </p>
        </div>

        {/* Lyrics Display Area */}
        <div className="h-full flex flex-col items-center justify-center px-8 pt-24 pb-32">
          {lyricsPlayer?.isLoading ? (
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-white mx-auto mb-4"></div>
              <p className="text-white text-lg">Loading lyrics...</p>
            </div>
          ) : lyricsPlayer?.error ? (
            <div className="text-center">
              <Icon name="AlertCircle" size={48} className="text-red-400 mx-auto mb-4" />
              <p className="text-red-300 text-lg">{lyricsPlayer?.error}</p>
            </div>
          ) : !lyricsPlayer?.hasLyrics ? (
            <div className="text-center">
              <Icon name="FileText" size={48} className="text-yellow-300 mx-auto mb-4" />
              <p className="text-white text-lg">No synchronized lyrics found in this MIDI file</p>
              <p className="text-blue-200 text-sm mt-2">
                This file may not contain embedded lyrics data
              </p>
            </div>
          ) : (
            <div className="w-full max-w-4xl space-y-6 overflow-y-auto max-h-full">
              {lyricsPlayer?.lyrics?.map((lyric, index) => {
                const isCurrent = index === lyricsPlayer?.currentLyricIndex;
                const isPast = index < lyricsPlayer?.currentLyricIndex;
                const isFuture = index > lyricsPlayer?.currentLyricIndex;

                return (
                  <div
                    key={index}
                    className={`
                      text-center transition-all duration-500 transform
                      ${isCurrent ? 'scale-125 opacity-100' : 'scale-100'}
                      ${isPast ? 'opacity-40' : ''}
                      ${isFuture ? 'opacity-60' : ''}
                    `}
                  >
                    <p
                      className={`
                        font-bold leading-relaxed transition-all duration-300
                        ${isCurrent ? 'text-5xl text-yellow-300 drop-shadow-2xl animate-pulse' : 'text-3xl text-white'}
                        ${isPast ? 'text-blue-300' : ''}
                      `}
                    >
                      {lyric}
                    </p>
                    {isCurrent && (
                      <div className="mt-2 w-full max-w-md mx-auto">
                        <div className="h-1 bg-white bg-opacity-20 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-yellow-400 to-orange-500 transition-all duration-100"
                            style={{ width: `${lyricsPlayer?.progress}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Control Bar */}
        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 backdrop-blur-md p-6">
          <div className="max-w-4xl mx-auto">
            {/* Progress Bar */}
            <div className="mb-4">
              <div className="h-2 bg-white bg-opacity-20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 transition-all duration-100"
                  style={{ width: `${lyricsPlayer?.progress || 0}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-blue-200 mt-1">
                <span>{Math.floor(lyricsPlayer?.progress || 0)}%</span>
                <span>100%</span>
              </div>
            </div>

            {/* Playback Controls */}
            <div className="flex items-center justify-center space-x-4">
              <button
                onClick={handleStop}
                disabled={lyricsPlayer?.isLoading}
                className="p-3 bg-red-600 hover:bg-red-700 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Stop"
              >
                <Icon name="Square" size={24} className="text-white" />
              </button>

              <button
                onClick={handlePlayPause}
                disabled={lyricsPlayer?.isLoading || !lyricsPlayer?.hasLyrics}
                className="p-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                aria-label={lyricsPlayer?.isPlaying ? 'Pause' : 'Play'}
              >
                <Icon 
                  name={lyricsPlayer?.isPlaying ? 'Pause' : 'Play'} 
                  size={32} 
                  className="text-white" 
                />
              </button>

              <button
                onClick={handleClose}
                className="p-3 bg-gray-600 hover:bg-gray-700 rounded-full transition-colors"
                aria-label="Close"
              >
                <Icon name="X" size={24} className="text-white" />
              </button>
            </div>

            {/* Status Info */}
            {audioPlayer?.loadingStatus && (
              <p className="text-center text-sm text-blue-200 mt-3">
                {audioPlayer?.loadingStatus}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}